import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import queryString from "querystring";
import { Form, FormRenderProps, useForm, useFormState } from "react-final-form";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import {
  replaceForFilterForm,
  replaceForURLOptions,
} from "../../helpers/helperFunctions";
import { useCardsPage } from "../../cardsPageContext";
import { FilterFormType } from "../../constants";
import FilterFormSecondary from "./FilterFormSecondary";
import FilterFormHigher from "./FilterFormHigher";
import FilterFormCourse from "./FilterFormCourse";

const useStyles = makeStyles(() => ({
  root: {
    width: 391,
    padding: "24px 16px",
    borderRadius: 15,
    backgroundColor: "#ffffff",
    boxShadow: "0 0 8px #C2C9D682",
    top: 119,
    position: "sticky",
  },
  container: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#231F20",
    lineHeight: "29px",
  },
  subTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#231F20",
    lineHeight: "17px",
    marginTop: 11,
  },
  tabs: {
    marginTop: 11,
  },
  tabLink: {
    "&:hover": {
      textDecoration: "none",
    },
    "& .MuiTab-wrapper": {
      alignItems: "self-start",
    },
    "&+&": {
      marginLeft: 16,
    },
    "&.MuiTab-root.Mui-selected": {
      fontSize: 14,
      fontWeight: "bold",
      color: "#231F20",
    },
    "&.MuiTab-textColorInherit": {
      fontSize: 14,
      color: "#858997",
    },
    "&.MuiTab-root": {
      padding: "unset",
      width: 108,
      minWidth: 108,
    },
  },
}));

const FilterForm: FunctionComponent<FormRenderProps> = ({ submitError }) => {
  const classes = useStyles();

  const { change } = useForm();
  const { values } = useFormState();
  const location = useLocation();
  const { handleFilterChange } = useCardsPage();
  const history = useHistory();

  const handleTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    localStorage.setItem("filterType", value.toString());
    change("filterType", value);
  };

  useEffect(() => {
    history.replace({
      pathname: location.pathname,
      search: "?filters=" + JSON.stringify(replaceForURLOptions(values)),
    });
    handleFilterChange?.(values);
  }, [values]);

  const getContent = useCallback((type) => {
    switch (type) {
      case FilterFormType.COURSE: {
        return <FilterFormCourse />;
      }
      case FilterFormType.SECONDARY: {
        return <FilterFormSecondary />;
      }
      case FilterFormType.HIGHER: {
        return <FilterFormHigher />;
      }
    }
  }, []);

  return (
    <form>
      {submitError && <Typography color="error">{submitError}</Typography>}
      <div>
        <Tabs
          value={values.filterType}
          className={classes.tabs}
          onChange={handleTabChange}
        >
          <Tab
            className={classes.tabLink}
            label={"Higher Education"}
            value={FilterFormType.HIGHER}
          />
          <Tab
            className={classes.tabLink}
            label={"Secondary Education"}
            value={FilterFormType.SECONDARY}
          />
          <Tab
            className={classes.tabLink}
            label={"Language Course"}
            value={FilterFormType.COURSE}
          />
        </Tabs>
        {getContent(values.filterType)}
      </div>
    </form>
  );
};

const Filter = () => {
  const classes = useStyles();

  const location = useLocation();
  const { handleFilterChange } = useCardsPage();
  const history = useHistory();

  let initialValues = useMemo(() => {
    let queryParams = queryString.parse(
      location.search.split("?").join(""),
      " ",
    );

    if (typeof queryParams.filters === "string") {
      let filters = JSON.parse(queryParams.filters);

      if (Object.keys(filters).length > 0) {
        return replaceForFilterForm(filters);
      }
    }
    const filterTypeFromStorage = localStorage.getItem("filterType");

    return {
      filterType: filterTypeFromStorage
        ? +filterTypeFromStorage
        : FilterFormType.HIGHER,
    };
  }, []);

  const submitHandler = useCallback(async (data?) => {
    history.replace({
      pathname: location.pathname,
      search: "?filters=" + JSON.stringify(replaceForURLOptions(data)),
    });
    handleFilterChange?.(data);
  }, []);

  useEffect(() => {
    submitHandler(initialValues);
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Typography className={classes.title} component={"h1"}>
          Filter
        </Typography>
        <Typography className={classes.subTitle} component={"h2"}>
          Search for Programs
        </Typography>
      </div>
      <Form
        initialValues={initialValues}
        onSubmit={submitHandler as any}
        keepDirtyOnReinitialize
        component={FilterForm}
      />
    </div>
  );
};
export default React.memo(Filter);
